import React, { useEffect, useMemo, useState } from 'react';
import {
  Col,
  ConfigProvider,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Spin,
  message,
  DatePicker,
} from 'antd';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';
import type { AdmVehicle, Company, ItModule, Personnel } from '../../../types';
import { fetchSqlDataWithQuery } from '../../../services/api';
import ContractSelect from '../../../components/modals/ContractSelect';
import Button from '../../../components/common/Button';

interface AdmVehicleFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (values: Partial<AdmVehicle> & { current_user_id?: number }) => void;
  initialValues?: AdmVehicle | null;
  companies: Company[];
  departments: ItModule[];
  personnel: Personnel[];
  isLoadingLookups: boolean;
}

const HOUR_BASED_MACHINE_TYPES = ['Excavator', 'BackhoeLoader', 'Forklift', 'Mobile Crane', 'Crawler Crane', 'Hi-Up', 'Manlift'];

const matchOption = (input: string, option?: { label?: string }) =>
  (option?.label ?? '').toLowerCase().includes(input.toLowerCase());

const nonNegativeInteger = (messageText: string) => ({
  validator: (_: unknown, value: number | null | undefined) => {
    if (value != null && (!Number.isInteger(value) || value < 0)) {
      return Promise.reject(new Error(messageText));
    }
    return Promise.resolve();
  },
});

const AdmVehicleForm: React.FC<AdmVehicleFormProps> = ({
  open,
  onClose,
  onSubmit,
  initialValues,
  companies,
  personnel: allPersonnel,
  isLoadingLookups,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [contractModalOpen, setContractModalOpen] = useState(false);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null | undefined>(initialValues?.company_id);
  const selectedMachineType = Form.useWatch('machine_type', form);
  const contractReference = Form.useWatch('contract_reference_id', form);

  const { data: machineTypeEnumRows = [], isLoading: isLoadingMachineTypes } = useQuery<any[], Error>({
    queryKey: ['enum', 'machine_type_enum'],
    queryFn: () => fetchSqlDataWithQuery('SELECT unnest(enum_range(NULL::machine_type_enum)) AS value'),
    staleTime: 1000 * 60 * 30,
  });

  const machineTypeOptions = useMemo(() => {
    const rows: any[] = Array.isArray(machineTypeEnumRows) ? machineTypeEnumRows : [];
    return rows.map((row) => ({ value: row.value, label: row.value }));
  }, [machineTypeEnumRows]);

  const showHoursCounter = HOUR_BASED_MACHINE_TYPES.includes(selectedMachineType);
  const ownershipOptions = [
    { value: 'CompanyOwned', label: 'Company Owned' },
    { value: 'Rented', label: 'Rented' },
    { value: 'Leased', label: 'Leased' },
  ];
  const statusOptions = [
    { value: 'Available', label: 'Available' },
    { value: 'Assigned', label: 'Assigned' },
    { value: 'InMaintenance', label: 'In Maintenance' },
    { value: 'OutOfService', label: 'Out of Service' },
    { value: 'Inactive', label: 'Inactive' },
  ];
  const fuelTypeOptions = [
    { value: 'Gasoline', label: 'Gasoline' },
    { value: 'Diesel', label: 'Diesel' },
    { value: 'LPG', label: 'LPG' },
    { value: 'Electric', label: 'Electric' },
    { value: 'Hybrid', label: 'Hybrid' },
  ];

  const filteredPersonnel = useMemo(
    () => (selectedCompanyId ? allPersonnel.filter((person) => person.company_id === selectedCompanyId) : []),
    [selectedCompanyId, allPersonnel],
  );

  useEffect(() => {
    if (!selectedCompanyId) return;
    const currentUserId = form.getFieldValue('current_user_id');
    if (currentUserId && !filteredPersonnel.some((person) => person.id === currentUserId)) {
      form.setFieldsValue({ current_user_id: undefined });
    }
  }, [selectedCompanyId, filteredPersonnel, form]);

  const handleCompanyChange = (value: number | undefined) => {
    setSelectedCompanyId(value);
    form.setFieldsValue({ current_user_id: undefined });
  };

  useEffect(() => {
    if (!open) return;
    if (initialValues) {
      const { seats: _seats, ...vehicleValues } = initialValues;
      form.setFieldsValue({
        ...vehicleValues,
        year: initialValues.year ?? undefined,
        kilometer: initialValues.kilometer ?? undefined,
        current_user_id: initialValues.operator_id ?? undefined,
        machine_type: initialValues.machine_type ?? undefined,
        hours_counter: initialValues.hours_counter ?? undefined,
        company_id: initialValues.company_id ?? undefined,
        passenger_capacity: initialValues.passenger_capacity ?? undefined,
        contract_reference_id: initialValues.contract_reference_id ?? undefined,
        arrival_date: initialValues.arrival_date ? dayjs(initialValues.arrival_date) : undefined,
        departure_date: initialValues.departure_date ? dayjs(initialValues.departure_date) : undefined,
        status: initialValues.departure_date ? 'Inactive' : initialValues.status,
      });
      setSelectedCompanyId(initialValues.company_id);
      return;
    }
    form.resetFields();
    form.setFieldsValue({ status: 'Available', ownership_type: 'CompanyOwned' });
    setSelectedCompanyId(null);
  }, [open, initialValues, form]);

  useEffect(() => {
    if (!selectedMachineType) return;
    if (!HOUR_BASED_MACHINE_TYPES.includes(selectedMachineType)) {
      form.setFieldsValue({ hours_counter: undefined });
    }
  }, [selectedMachineType, form]);

  const handleFinish = async (values: Partial<AdmVehicle> & { current_user_id?: number; arrival_date?: Dayjs | string; departure_date?: Dayjs | string | null }) => {
    setLoading(true);
    try {
      const contractReference = typeof values.contract_reference_id === 'string'
        ? values.contract_reference_id.trim()
        : '';
      const arrival = values.arrival_date;
      const departure = values.departure_date;
      const { seats: _seats, ...vehicleValues } = values;
      await onSubmit({
        ...vehicleValues,
        arrival_date: dayjs.isDayjs(arrival) ? arrival.format('YYYY-MM-DD') : arrival,
        departure_date: departure ? (dayjs.isDayjs(departure) ? departure.format('YYYY-MM-DD') : departure) : null,
        status: departure ? 'Inactive' : vehicleValues.status,
        contract_reference_id: contractReference || null,
      });
    } catch (err: any) {
      message.error(`Submission failed: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const companyOptions = companies.map((company) => ({ value: company.id, label: company.name }));
  const personnelOptions = filteredPersonnel.map((person) => ({
    value: person.id,
    label: `${person.first_name} ${person.last_name}`.trim(),
  }));
  const isEditing = Boolean(initialValues?.id);
  const currentYear = new Date().getFullYear() + 1;

  return (
    <Drawer
      title={isEditing ? 'Edit Vehicle' : 'Add New Vehicle'}
      width={680}
      onClose={onClose}
      open={open}
      zIndex={3000}
      destroyOnClose
      footer={(
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose}>{'Cancel'}</Button>
          <Button action={isEditing ? 'edit' : 'add'} type="primary" loading={loading} onClick={() => form.submit()}>
            {isEditing ? 'Update Vehicle' : 'Add Vehicle'}
          </Button>
        </Flex>
      )}
    >
      <ConfigProvider theme={{ components: { Form: { itemMarginBottom: 12, verticalLabelPadding: '0 0 2px' } } }}>
        <Spin spinning={isLoadingLookups || loading}>
          <Form form={form} layout="vertical" size="small" onFinish={handleFinish}>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="license_plate"
                  label={'License Plate'}
                  rules={[
                    { required: true, message: 'Please enter license plate' },
                    { transform: (value) => value?.trim() },
                  ]}
                >
                  <Input placeholder={'Enter license plate'} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="machine_type"
                  label={'Machine Type'}
                  rules={[{ required: true, message: 'Please select vehicle type' }]}
                >
                  <Select
                    options={machineTypeOptions}
                    loading={isLoadingMachineTypes}
                    showSearch
                    optionFilterProp="label"
                    placeholder={isLoadingMachineTypes
                      ? 'Loading...'
                      : 'Select vehicle type'}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="make"
                  label={'Make'}
                  rules={[{ transform: (value) => value?.trim() || undefined }]}
                >
                  <Input placeholder={'Enter make'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="model"
                  label={'Model'}
                  rules={[{ transform: (value) => value?.trim() || undefined }]}
                >
                  <Input placeholder={'Enter model'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="year"
                  label={'Year'}
                  rules={[{
                    validator: (_, value) => {
                      if (value != null && (!Number.isInteger(value) || value < 1900 || value > currentYear)) {
                        return Promise.reject(new Error('Please enter a valid year'));
                      }
                      return Promise.resolve();
                    },
                  }]}
                >
                  <InputNumber style={{ width: '100%' }} min={1900} max={currentYear} step={1} placeholder={'Enter year'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="kilometer"
                  label={'Kilometer'}
                  rules={[nonNegativeInteger('Please enter a valid non-negative number')]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} step={1} placeholder={'Enter kilometer'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="fuel_type" label={'Fuel Type'}>
                  <Select options={fuelTypeOptions} allowClear placeholder={'Select fuel type'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="status" label={'Status'} initialValue="Available">
                  <Select options={statusOptions} allowClear placeholder={'Select status'} />
                </Form.Item>
              </Col>
              {showHoursCounter && (
                <Col span={8}>
                  <Form.Item
                    name="hours_counter"
                    label={'Hours Counter'}
                    rules={[nonNegativeInteger('Please enter a valid number')]}
                    extra={'Used for hour-based usage and maintenance tracking on machine-type assets.'}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} step={1} placeholder={'Enter current hours'} />
                  </Form.Item>
                </Col>
              )}
              <Col span={8}>
                <Form.Item
                  name="passenger_capacity"
                  label={'Passenger Capacity'}
                  rules={[nonNegativeInteger('Please enter a non-negative integer')]}
                >
                  <InputNumber style={{ width: '100%' }} min={0} step={1} placeholder={'Enter passenger capacity'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="ownership_type"
                  label={'Ownership Type'}
                  initialValue="CompanyOwned"
                  rules={[{ required: true, message: 'Please select ownership type' }]}
                >
                  <Select options={ownershipOptions} placeholder={'Select ownership type'} />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="company_id" label={'Company'}>
                  <Select
                    options={companyOptions}
                    loading={isLoadingLookups}
                    onChange={handleCompanyChange}
                    showSearch
                    allowClear
                    placeholder={'Select company'}
                    filterOption={matchOption}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item name="current_user_id" label={'Current User'}>
                  <Select
                    options={personnelOptions}
                    loading={isLoadingLookups}
                    showSearch
                    allowClear
                    disabled={!selectedCompanyId}
                    placeholder={selectedCompanyId
                      ? 'Select current user'
                      : 'Select company first'}
                    filterOption={matchOption}
                    notFoundContent={isLoadingLookups
                      ? <Spin size="small" />
                      : (selectedCompanyId
                        ? 'No personnel found for this company'
                        : 'Select a company')}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="arrival_date"
                  label={'Arrival Date'}
                  rules={[{ required: true, message: 'Please enter the arrival date' }]}
                >
                  <DatePicker style={{ width: '100%' }} format="DD/MM/YYYY" />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="departure_date"
                  label={'Departure Date'}
                  dependencies={['arrival_date']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const arrival = getFieldValue('arrival_date');
                        if (!value || !arrival) return Promise.resolve();
                        if (dayjs(value).startOf('day').isBefore(dayjs(arrival).startOf('day'))) {
                          return Promise.reject(new Error('Departure date cannot be earlier than the arrival date'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <DatePicker
                    style={{ width: '100%' }}
                    format="DD/MM/YYYY"
                    disabledDate={(current) => {
                      const arrival = form.getFieldValue('arrival_date');
                      if (!current || !arrival) return false;
                      return current.startOf('day').isBefore(dayjs(arrival).startOf('day'));
                    }}
                    onChange={(date) => {
                      const arrival = form.getFieldValue('arrival_date');
                      const isBeforeArrival = Boolean(
                        date && arrival && date.startOf('day').isBefore(dayjs(arrival).startOf('day'))
                      );
                      if (date && !isBeforeArrival) form.setFieldsValue({ status: 'Inactive' });
                      }}
                  />
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item
                  name="contract_reference_id"
                  label={'Reference'}
                  rules={[
                    { max: 50, message: 'Contract reference must be at most 50 characters' },
                    { transform: (value) => (typeof value === 'string' ? value.trim() || undefined : value) },
                  ]}
                >
                  <Input
                    readOnly
                    maxLength={50}
                    allowClear
                    placeholder={'Select a contract'}
                    addonAfter={(
                      <Button type="link" size="small" onClick={() => setContractModalOpen(true)}>
                        {'Select'}
                      </Button>
                    )}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Form>
          <ContractSelect
            open={contractModalOpen}
            value={contractReference ? { reference_id: contractReference } : undefined}
            onCancel={() => setContractModalOpen(false)}
            onOk={(selected) => {
              form.setFieldsValue({ contract_reference_id: selected.reference_id || undefined });
              setContractModalOpen(false);
            }}
          />
        </Spin>
      </ConfigProvider>
    </Drawer>
  );
};

export default AdmVehicleForm;
